import { FC, useContext, useState } from 'react';
import { If, PortalPopover } from 'components';
import { ListActionsIcon } from 'assets/icons';
import MoveJobToPayRunModal from '../../components/MoveJobToPayRunModal';
import { useMoveJobToPayRunMutation } from '../../api';
import { getNotifications, selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks/typedHooks';
import { useParams } from 'react-router-dom';
import { JobReviewStatus, PayRunStatus } from '../../constants';
import { getJobReviewStatusMap } from '../../utils';
import { useUpdateJobStatusMutation } from '../api';
import { selectSelectedReviewTab } from '../confirmJobsSlice';
import { PayRunContext } from '../../MemberReview';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../../events';
import { NotifierTypes } from 'constants/notifierConstants';

interface JobReviewActionsProps {
  jobId: string;
  jobNumber: string;
  fetchData: (page: number) => void;
  hasDraftAdjustment: boolean;
}

const JobReviewActions: FC<JobReviewActionsProps> = ({ jobId, jobNumber, fetchData, hasDraftAdjustment }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { payRunId, merchantId } = useParams();
  const selectedReviewTab = useAppSelector(selectSelectedReviewTab);
  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();

  // Get payRunStatus from context
  const { payRunStatus } = useContext(PayRunContext);

  const [showActionsPopover, setShowActionsPopover] = useState(false);
  const [isMoveJobToPayRunModalOpen, setIsMoveJobToPayRunModalOpen] = useState(false);

  const actionsButton = ({ isPanelOpen }: { isPanelOpen: boolean }) => (
    <button className="z-20 rounded-full p-2 hover:bg-gray-100">
      <ListActionsIcon />
    </button>
  );

  const handleMoveToOpenPayRun = () => {
    setIsMoveJobToPayRunModalOpen(true);
    setShowActionsPopover(false);
  };

  const [moveJobToPayRun, { isLoading: isMoveJobToPayRunLoading }] = useMoveJobToPayRunMutation();
  const [updateJobStatus] = useUpdateJobStatusMutation();

  const getStatusOptions = () => {
    const relevantStatuses = [
      JobReviewStatus.IN_PROGRESS,
      JobReviewStatus.CONFIRMED,
      JobReviewStatus.SKIPPED,
      JobReviewStatus.NEEDS_REVIEW
    ];

    const allOptions = relevantStatuses.map(status => ({
      value: status,
      label:
        status === JobReviewStatus.SKIPPED
          ? getJobReviewStatusMap(status).label
          : `Mark as ${getJobReviewStatusMap(status)?.label.toLowerCase()}`
    }));

    return allOptions.filter(option => option.value !== (selectedReviewTab as string));
  };

  const showInfoNotifier = () => {
    const notification = notifications.find(n => n.id === 'job-review-actions');
    if (notification) {
      return;
    }
    dispatch(
      showNotifier({
        id: 'job-review-actions',
        type: NotifierTypes.WARNING,
        message: { primaryMessage: 'You cannot confirm this job because it has pending adjustments.' },
        showClose: true
      })
    );
  };

  const handleStatusChange = (status: string) => {
    if (status === JobReviewStatus.CONFIRMED && hasDraftAdjustment) {
      showInfoNotifier();
      return;
    }

    updateJobStatus({
      teamId: currentTeam?.id.toString(),
      payRunId,
      merchantId: merchantId as string,
      status,
      jobId
    })
      .unwrap()
      .then(() => {
        setShowActionsPopover(false);
        fetchData(1);
        logAnalyticEvent(PayRunsCleverTapEvents.webPayRunJobStatusChange, {
          payRunId,
          merchantId,
          status,
          jobId
        });
      });
  };

  const handleMove = (selectedPayRun: string) => {
    moveJobToPayRun({
      teamId: currentTeam?.id,
      sourcePayRunId: payRunId,
      targetPayRunId: selectedPayRun,
      jobId
    })
      .unwrap()
      .then(() => {
        setIsMoveJobToPayRunModalOpen(false);
        fetchData(1);
      });
  };

  return (
    <div className="relative">
      <PortalPopover
        anchorComponent={actionsButton}
        show={showActionsPopover}
        onTogglePopover={() => setShowActionsPopover(!showActionsPopover)}
        onClosePopover={() => setShowActionsPopover(false)}
        offset={{ right: 0, top: -165 }}
        padding="p-0 pr-[6px]"
        showArrow={false}
        arrowOffset="top-3"
        width="w-[205px]">
        <div className="z-20 flex flex-col py-2">
          {getStatusOptions().map(option => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className="px-4 py-2 text-left text-sbase font-semibold text-primaryText hover:bg-gray-100">
              {option.label}
            </button>
          ))}
          <If condition={payRunStatus !== PayRunStatus.COMPLETE}>
            <button
              onClick={handleMoveToOpenPayRun}
              className="px-4 py-2 text-left text-sbase font-semibold text-primaryText hover:bg-gray-100">
              Move to an open pay run
            </button>
          </If>
        </div>
      </PortalPopover>
      <MoveJobToPayRunModal
        isOpen={isMoveJobToPayRunModalOpen}
        onClose={() => setIsMoveJobToPayRunModalOpen(false)}
        handleMove={handleMove}
        jobNumber={jobNumber}
        currentPayRunId={payRunId}
        isMoveJobLoading={isMoveJobToPayRunLoading}
      />
    </div>
  );
};

export default JobReviewActions;
